import React from 'react';
import { classNames } from '../utils/classNames';

// A row of small bars, one bar for each run of a recurring job, the newest run on the right.
// The tone of a bar tells the result of that run: success (emerald), warning (amber), critical (rose),
// none (gray: no result yet, for example a run that is still in progress). The caller gives each bar
// its hover text ("06/10 09:00 · Success"), which is also its accessible name.
// The strip shows the last `max` runs only. It knows nothing about the job behind the runs.
export type RunTone = 'success' | 'warning' | 'critical' | 'none';

export type Run = {
	tone: RunTone,
	title: string,
};

type Props = {
	// The accessible name of the strip ("Last 14 runs of the Availability integration").
	label: string,
	// The runs, oldest first.
	runs: Run[],
	max?: number,
	// The text when there is no run. Empty: the strip shows nothing.
	emptyLabel?: string,
};

const TONE: Record<RunTone, string> = {
	success: 'bg-emerald-500',
	warning: 'bg-amber-500',
	critical: 'bg-rose-500',
	none: 'bg-gray-200',
};

const RunHistoryStrip: React.FunctionComponent<Props> = ({ label, runs, max = 14, emptyLabel = '' }) => {
	const shown = runs.slice(-max);
	if (shown.length === 0) {
		return emptyLabel === '' ? null : <span className="text-xs text-gray-400">{emptyLabel}</span>;
	}
	return <ul aria-label={label} className="inline-flex shrink-0 items-center gap-0.5">
		{shown.map((run, index) => <li key={`${index}-${run.title}`} title={run.title} className="flex">
			<span className="sr-only">{run.title}</span>
			<span aria-hidden="true" className={classNames('h-3.5 w-1.5 rounded-sm', TONE[run.tone])} />
		</li>)}
	</ul>;
};

export default RunHistoryStrip;
